/** REQ-TESTDATA-004 (v0.8.1) — locale-aware decimal literal parsing/rendering
 * for precision-test line inputs.
 *
 * This is presentation-layer number formatting/parsing only (built on the
 * browser's own `Intl` locale data) — it never reimplements or duplicates
 * the backend's deterministic precision-generation algorithm. Its only job
 * is to preserve exactly what the user sees/types (including trailing
 * zeros, e.g. `12,5000` vs `12,5`), which a plain `<input type="number">`
 * cannot do (it silently normalizes/strips trailing zeros and does not
 * accept a comma decimal separator in most browsers).
 */

function localeSeparators(locale: string): { decimal: string; group: string } {
  const parts = new Intl.NumberFormat(locale).formatToParts(1234.5)
  const decimal = parts.find(p => p.type === 'decimal')?.value ?? '.'
  const group = parts.find(p => p.type === 'group')?.value ?? ','
  return { decimal, group }
}

/** Render `value` at exactly `scale` fractional digits using the locale's own
 * decimal/grouping separators. Never trims trailing zeros. */
export function formatDecimalLiteral(value: number, scale: number, locale: string): string {
  const { decimal, group } = localeSeparators(locale)
  const safeScale = Math.max(0, Math.min(6, Math.round(scale)))
  const fixed = value.toFixed(safeScale) // ASCII "1234.5000"
  const [intPart, fracPart] = fixed.split('.')
  const sign = intPart.startsWith('-') ? '-' : ''
  const digits = sign ? intPart.slice(1) : intPart
  const groupedInt = digits.replace(/\B(?=(\d{3})+(?!\d))/g, group)
  return fracPart ? `${sign}${groupedInt}${decimal}${fracPart}` : `${sign}${groupedInt}`
}

/** Parse a locale-formatted literal back into `{ value, scale }`. Strips the
 * locale's grouping separator, then derives BOTH the numeric value and the
 * intended scale from the digit count actually typed after the decimal
 * separator — so `2,5000` round-trips as `value=2.5, scale=4`, not silently
 * normalized to `scale=1`. Returns `null` for input that cannot (yet) be
 * parsed (e.g. empty, or only a sign/separator while the user is mid-typing). */
export function parseDecimalLiteral(raw: string, locale: string): { value: number; scale: number } | null {
  const { decimal, group } = localeSeparators(locale)
  const cleaned = raw.trim()
  if (!cleaned) return null
  const withoutGroups = group ? cleaned.split(group).join('') : cleaned
  const normalized = decimal === '.' ? withoutGroups : withoutGroups.split(decimal).join('.')
  if (!/^-?\d+\.?\d*$/.test(normalized)) return null
  const value = Number(normalized)
  if (!Number.isFinite(value)) return null
  const fracPart = normalized.split('.')[1]
  const scale = fracPart ? fracPart.length : 0
  return { value, scale }
}
